/*
axiosClient — central axios instance.

KAN-39 interceptor + axios-retry ordering
-----------------------------------------
Two separate mechanisms attach to the same global `axios` in this file:

  (1) axios-retry: installed via `axiosRetry(axios, …)` below. It wraps
      `axios.request` to schedule retries based on its `retryCondition`.
  (2) Our own request / response interceptors (added further down)
      that inject `Authorization: Bearer <jwt>`, refresh on 401, and
      surface the session-expired reason.

Ordering for a request:
  - The request interceptor runs first (axios resolves its interceptor
    chain before dispatching), so we get a chance to proactively
    refresh + stamp the Authorization header.
  - If the response returns an error, axios-retry's wrapper decides
    whether to reschedule; our response-rejected interceptor also
    runs. Critically, the retryCondition below restricts retries to
    `isNetworkOrIdempotentRequestError` (timeouts, ECONNRESET, etc.)
    OR 5xx responses. A 401 is **not** network/idempotent and is not
    >= 500, so axios-retry DOES NOT fire on the paths our refresh
    interceptor handles — there is no double-retry of /api/* on 401.
  - The `__refreshRetried` flag stamped on the config is our own
    single-shot guard independent of axios-retry's counter.

If someone later widens `retryCondition` to include 4xx, collisions
with `__refreshRetried` become possible; please re-evaluate before
merging such a change.

Note: the test build aliases axios-retry to a no-op stub
(see build/webpack.test.conf.js) because is-retry-allowed ships ES6
PhantomJS cannot parse. The prod bundle uses the real lib.
*/

import axios from 'axios'
import axiosRetry from 'axios-retry/dist/cjs/index'

import {
  getAccessToken,
  getRefreshToken,
  getClaims,
  refresh as refreshSession,
  clearTokens,
  setPostLoginRedirect,
  isLoginInFlight
} from '@/lib/auth'
import { JWT_PROACTIVE_REFRESH_SECONDS } from '@/lib/env'

// XOL-144: cross-origin cookie support for XOL-145's xolas_auth session
// cookie. Without this, the browser's "omit credentials" default discards
// every Set-Cookie on the response and drops cookies from outgoing requests.
// Paired with API CORS supports_credentials=true and SameSite=None;Secure
// on the cookie in dev (cross-origin :8080↔:8000); SameSite=Lax in prod
// (same-origin SPA + API).
axios.defaults.withCredentials = true

// Configure axios-retry to retry 3 times on failure
axiosRetry(axios, {
  retries: 3,
  // Exponential backoff: 100ms, 200ms, 400ms, 800ms, 1600ms
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    // Retry only on network errors or 5xx server errors.
    // IMPORTANT: 401 must NOT land here — the refresh+retry dance lives
    // in the response interceptor below. See the module header for why
    // this ordering is safe.
    return axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      (error.response && error.response.status >= 500)
  }
})

// Function to generate a unique ID
function generateRequestId () {
  // Use crypto.randomUUID() for a standard UUID (Node.js/modern browsers)
  // or a simple alternative if needed
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  } else {
    // Fallback for older environments
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
  }
}

const REFRESH_PATH = '/api/auth/refresh'
const LOGIN_PATH = '/api/login'

// Strip the query string + hash so the suffix check below is unambiguous.
function pathnameOf (url) {
  if (!url) {
    return ''
  }
  let out = String(url)
  const hashIdx = out.indexOf('#')
  if (hashIdx !== -1) {
    out = out.slice(0, hashIdx)
  }
  const queryIdx = out.indexOf('?')
  if (queryIdx !== -1) {
    out = out.slice(0, queryIdx)
  }
  return out
}

// Explicit suffix check: PhantomJS 2 (our karma browser) does NOT ship
// String.prototype.endsWith, so avoid it in the prod bundle.
function endsWithStr (haystack, needle) {
  if (!haystack || !needle) {
    return false
  }
  if (needle.length > haystack.length) {
    return false
  }
  return haystack.lastIndexOf(needle) === (haystack.length - needle.length)
}

// Exact-suffix match on pathname — substring matches falsely exempted
// sibling endpoints such as `/api/auth/refreshPassword` or
// `/api/loginHistory`, which would have skipped the Authorization
// header in prod.
function isAuthExemptUrl (url) {
  if (!url) {
    return false
  }
  const path = pathnameOf(url)
  if (!path) {
    return false
  }
  return (
    path === REFRESH_PATH ||
    endsWithStr(path, REFRESH_PATH) ||
    path === LOGIN_PATH ||
    endsWithStr(path, LOGIN_PATH)
  )
}

// -- Single-flight refresh ----------------------------------------------------
// Concurrent proactive/reactive callers share one in-flight promise.
let inFlightRefresh = null

function singleFlightRefresh () {
  if (inFlightRefresh) {
    return inFlightRefresh
  }
  inFlightRefresh = refreshSession()
    .then(function (result) {
      inFlightRefresh = null
      return result
    }, function (err) {
      inFlightRefresh = null
      throw err
    })
  return inFlightRefresh
}

function shouldProactivelyRefresh () {
  const claims = getClaims()
  if (!claims || !claims.exp) {
    return false
  }
  const nowSec = Math.floor(Date.now() / 1000)
  const remaining = Number(claims.exp) - nowSec
  return remaining > 0 && remaining < Number(JWT_PROACTIVE_REFRESH_SECONDS)
}

export const LOGOUT_REASON_KEY = 'xolas-logout-reason'
export const LOGOUT_REASON_SESSION_EXPIRED = 'session-expired'
export const LOGOUT_REASON_API_ACCESS_DENIED = 'api-access-denied'

// Auth-failure body code emitted by providence's auth-layer middleware.
// Providence also emits `X-Auth-Failure: <code>` on the wire, but CORS
// `exposed_headers` is empty on the providence side (XOL-192), so the
// header is filtered before axios can read it. We read the response body
// instead. XOL-192 tracks the providence CORS change; once that ships we
// can switch back to header-based detection (more semantically clean —
// body shape isn't a stable contract). For now, read the body.
const AUTH_FAILURE_CLIENT_GATE = 'client_gate'

// Tracks whether the most recent forced-logout was a client_gate reject
// (vs. a session-expired). The reactive-retry error handler reads this
// to avoid overwriting an 'api-access-denied' flash with 'session-expired'
// when a single-flight refresh itself gate-rejects mid-flight.
let lastForcedLogoutWasGate = false

// Detects the auth-failure family marker from the response body's `error`
// field. Providence also emits `X-Auth-Failure: <code>` on the wire (see
// XolasClientGate / XolasJwtAuth etc.), but CORS exposed_headers is empty
// on the providence side as of XOL-191, so the header is filtered before
// axios can read it. XOL-192 tracks the providence CORS change; once that
// ships we can switch back to header-based detection (more semantically
// clean — body shape isn't a stable contract). For now, read the body.
function getAuthFailureCode (error) {
  if (!error || !error.response || !error.response.data) return null
  const code = error.response.data.error
  return typeof code === 'string' ? code : null
}

function setLogoutReason (reason) {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem(LOGOUT_REASON_KEY, reason)
    }
  } catch (e) {
    // ignore quota / private-mode failures
  }
}

function redirectToLogin (reason) {
  // XOL-370: a login is currently in flight (canLogin's post-/api/login
  // chain — updateConfig()/refreshStats() — hasn't finished navigating
  // yet). A racing bootstrap 401 in that window must not clobber the
  // just-issued tokens or bounce the user back to /login with a
  // session-expired flash. Skip every forced-logout side effect below and
  // let the in-flight login finish; if that login's own network calls are
  // themselves failing terminally, canLogin's .catch clears the flag and
  // the user sees the ordinary invalid-credentials/login-failed message
  // instead of a phantom "session expired" banner.
  var loginInFlight = isLoginInFlight()
  if (loginInFlight) {
    return
  }
  // A "session expired" flash should only appear when the browser actually
  // had tokens going in. On boot, schema/config probes 401 with no JWT
  // present — that's "not signed in yet", not "just got signed out" — so
  // we must not persist a reason for that case. Capture the signal before
  // clearing, then clear, then set the flash only if a session existed.
  const hadSession = !!getAccessToken() || !!getRefreshToken()
  // Save the current in-app route so the user lands there after re-login.
  // Only save when a session actually existed (avoids storing redirect for
  // unauthenticated probes). sanitizeRedirectTarget (called inside
  // setPostLoginRedirect) will reject /login, /logout, and empty strings.
  if (hadSession && typeof window !== 'undefined' && window.location) {
    try {
      var hash = window.location.hash || ''
      // hash is "#/some/path" — strip the leading "#"
      var hashPath = hash.charAt(0) === '#' ? hash.slice(1) : hash
      if (hashPath) {
        setPostLoginRedirect(hashPath)
      }
    } catch (e) {
      // ignore
    }
  }
  clearTokens()
  // Acceptance #11: on a forced logout, surface a visible reason to the
  // user. Login.vue reads this flag on mount and shows a message.
  if (reason && hadSession) {
    setLogoutReason(reason)
  }
  if (typeof window !== 'undefined' && window.location) {
    try {
      // Router runs in hash mode (see src/router/index.js), so /login lives
      // in the hash, not the pathname. Hard-navigate to /#/login to avoid
      // leaving /login in the server pathname (which would otherwise stack
      // with the router's own beforeEach redirect, producing /login#/login).
      const hash = window.location.hash || ''
      if (hash.indexOf('/login') === -1) {
        window.location.assign('/#/login')
      }
    } catch (e) {
      // ignore
    }
  }
}

// -- Request interceptor ------------------------------------------------------
axios.interceptors.request.use(
  (config) => {
    // Per-request correlation ID
    if (!config.headers) {
      config.headers = {}
    }
    config.headers['X-Request-Id'] = generateRequestId()

    const url = config.url || ''
    if (isAuthExemptUrl(url)) {
      // /api/login & /api/auth/refresh: never inject Authorization, never refresh.
      return config
    }

    // Proactive refresh: if access exp - now < threshold AND a refresh token
    // is present, await the single-flight refresh before sending.
    const refreshToken = getRefreshToken()
    if (refreshToken && shouldProactivelyRefresh()) {
      return singleFlightRefresh()
        .then(function () {
          const access = getAccessToken()
          if (access) {
            config.headers['Authorization'] = 'Bearer ' + access
          }
          return config
        }, function (err) {
          // Refresh failed: send the request with the (likely-expired) token
          // so the response interceptor can handle the 401 path consistently.
          // Logged so ops has a breadcrumb when proactive refresh degrades.
          try { console.warn('proactive refresh failed', err) } catch (e) { /* ignore */ }
          const access = getAccessToken()
          if (access) {
            config.headers['Authorization'] = 'Bearer ' + access
          }
          return config
        })
    }

    const access = getAccessToken()
    if (access) {
      config.headers['Authorization'] = 'Bearer ' + access
    }
    return config
  },
  (error) => Promise.reject(error)
)

// -- Response interceptor -----------------------------------------------------
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error && error.config ? error.config : null
    const status = error && error.response ? error.response.status : null

    if (!config || status !== 401) {
      return Promise.reject(error)
    }

    // NEW: client_gate 401 — the SPA's X-API-Key is not accepted by the API.
    // Detected via the response body `error` field (body-based detection —
    // see getAuthFailureCode comment and XOL-192 for the future switch to
    // the cleaner header-based path). Refresh-then-retry won't help (refresh
    // hits the same gate), and the user can't fix this by re-entering
    // credentials. Clear tokens and surface a distinct flash on /login.
    if (getAuthFailureCode(error) === AUTH_FAILURE_CLIENT_GATE) {
      lastForcedLogoutWasGate = true
      redirectToLogin(LOGOUT_REASON_API_ACCESS_DENIED)
      return Promise.reject(error)
    }

    const url = config.url || ''
    if (isAuthExemptUrl(url)) {
      // 401 on /api/login or /api/auth/refresh => terminal. Don't recurse.
      const path = pathnameOf(url)
      if (path === REFRESH_PATH || endsWithStr(path, REFRESH_PATH)) {
        redirectToLogin(LOGOUT_REASON_SESSION_EXPIRED)
      }
      return Promise.reject(error)
    }

    if (config.__refreshRetried) {
      // Already retried once after a successful refresh. Terminal.
      redirectToLogin(LOGOUT_REASON_SESSION_EXPIRED)
      return Promise.reject(error)
    }

    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      redirectToLogin(LOGOUT_REASON_SESSION_EXPIRED)
      return Promise.reject(error)
    }

    config.__refreshRetried = true

    return singleFlightRefresh().then(function () {
      const access = getAccessToken()
      if (access) {
        if (!config.headers) {
          config.headers = {}
        }
        config.headers['Authorization'] = 'Bearer ' + access
      }
      return axios(config)
    }, function () {
      // Race guard: if the single-flight refresh itself hit a client_gate 401
      // and we already wrote the api-access-denied flash above, don't
      // overwrite it with session-expired.
      if (!lastForcedLogoutWasGate) {
        redirectToLogin(LOGOUT_REASON_SESSION_EXPIRED)
      }
      return Promise.reject(error)
    })
  }
)

// -- GET de-duplication (preserved from previous implementation) -------------
const originalGet = axios.get

function generateRequestKey (url, config) {
  const keyData = { url }
  if (config && config.params) {
    keyData.params = config.params
  }
  return JSON.stringify(keyData)
}

const pendingRequests = {}
axios.get = function (url, config) {
  const requestId = generateRequestKey(url, config)

  if (pendingRequests[requestId]) {
    console.log('request queued:', requestId)
    return new Promise(pendingRequests[requestId].resolver)
  }

  const requests = []
  const resolver = (resolve, reject) => {
    requests.push({ resolve, reject })
  }
  const res = originalGet(url, config)
  pendingRequests[requestId] = {
    res,
    requests,
    resolver
  }

  res.then((data) => {
    requests.forEach((request) => request.resolve(data))
    pendingRequests[requestId] = null
  })
  res.catch((err) => {
    requests.forEach((request) => request.reject(err))
    pendingRequests[requestId] = null
  })

  return new Promise(pendingRequests[requestId].resolver)
}

// Test-only hook to reset the single-flight promise between specs.
// Gated so the helper does not ship in a production bundle. Webpack's
// DefinePlugin inlines `process.env.NODE_ENV`, letting UglifyJS drop
// the branch entirely during prod minification.
export const _resetRefreshStateForTests = (process.env.NODE_ENV !== 'production')
  ? function _resetRefreshStateForTests () {
    inFlightRefresh = null
    lastForcedLogoutWasGate = false
  }
  : undefined

export default axios
